import React from "react";
import { observer } from "mobx-react";
import PolicyStore from "../../../../../stores/policy-store";
import currentPortalStore from "../../../../../../../stores/current-portal-store";
import { FEATURES } from "../../../../../../../models/Plan";
import { IOption, SelectDropdown } from "../../../../../../../components/inputs/select-dropdown/select-dropdown";
import { FormFieldLine } from "../../../../../../../components/form-field-line/form-field-line";
import FontBody2 from "../../../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR } from "../../../../../../../components/fonts/font-props";
import { AUTO_MARK_DONE, AUTO_PROCESS_EXECUTE, SINCE_UNIT, autoMarkAsDoneOptionsList, getAutoProcessSinceValueOptions } from "../../../../../constants";
import CheckboxesTreeDropdown from "../../../../../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree-dropdown";
import { IValidationGroup } from "../../../../../../../hooks/validation-group/validation-group-hook";

interface IProps {
    policy: PolicyStore;
    validationGroup: IValidationGroup;
}

const MarkAsDone: React.FC<IProps> = observer(({policy, validationGroup}) => {
    const markAsDoneAllowed = currentPortalStore.isFeatureAllowed(FEATURES.MARK_AS_DONE);
    const autoProcessSinceValueOptions = getAutoProcessSinceValueOptions(policy.executeSinceUnit);

    const selectedMarkAsDoneOption = autoMarkAsDoneOptionsList().find(o => o.value === policy.autoMarkDone);
    const selectedProcessSinceValueOption = autoProcessSinceValueOptions.find(o => o.value === policy.executeSinceValue);

    const executeOptions: IOption[] = [
        { value: AUTO_PROCESS_EXECUTE.IMMEDIATELY, label: 'Immediate' },
        { value: AUTO_PROCESS_EXECUTE.SINCE_TRIGGERED, label: 'After…' },
    ];

    const executeDelayUnitOptions: IOption[] = [
        { value: SINCE_UNIT.HOURS, label: 'Hours' },
        { value: SINCE_UNIT.DAYS, label: 'Days' },
    ];

    const getTooltipText = () => {
        if (!markAsDoneAllowed) {
            return (
                <>
                    Sorry, this feature is not included <br />
                    in your subscription plan
                </>
            );
        }

        return (
            <>
                For RMAs with multiple resolutions, the RMA will be <br />
                automatically marked as done only once all policy rules <br />
                have been fulfilled. RMAs must be manually marked as done <br />
                if they contain any policies configured to be manually marked as done.
            </>
        );
    }

    return (
        <>
            <FormFieldLine
                label='Mark as Done'
                note='Automate to mark RMAs as done automatically once all processing actions have been completed.'
                tooltip={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTooltipText()}</FontBody2>}
                disabled={!markAsDoneAllowed}
            >
                <SelectDropdown
                    value={selectedMarkAsDoneOption}
                    options={autoMarkAsDoneOptionsList()}
                    onChange={(v) => policy.setAutoMarkDone(v?.value as AUTO_MARK_DONE)}
                    disabled={!markAsDoneAllowed}
                    isSearchable={false}
                />
            </FormFieldLine>

            {selectedMarkAsDoneOption?.value === AUTO_MARK_DONE.AUTOMATICALLY && (
	            <>
		            <FormFieldLine label="Trigger">
			            <CheckboxesTreeDropdown
				            value={policy.getAutoMarkDoneTriggerOptionsTree()}
				            options={policy.getAutoMarkDoneTriggersOptions}
				            onChange={(v) => policy.setAutoMarkDoneTrigger(v ?? [])}
				            searchInputPlaceholder=""
				            isInitiallyAllItemsExpanded
			            />
		            </FormFieldLine>

		            <FormFieldLine label="Execute">
			            <SelectDropdown
				            value={executeOptions.find((o) => o.value === policy.selectedExecuteOption)}
				            options={executeOptions}
				            onChange={(v) => policy.setSelectedExecuteOption(v?.value as AUTO_PROCESS_EXECUTE)}
				            isSearchable={false}
			            />
		            </FormFieldLine>

		            {policy.selectedExecuteOption === AUTO_PROCESS_EXECUTE.SINCE_TRIGGERED && (
			            <FormFieldLine label="">
				            <div className='execute-since-fields'>
					            <SelectDropdown
						            value={selectedProcessSinceValueOption}
						            options={autoProcessSinceValueOptions}
						            onChange={(v) => policy.setExecuteSinceValue(v?.value as number)}
						            validationGroup={validationGroup}
						            required
						            isSearchable={false}
					            />
					            <SelectDropdown
						            value={executeDelayUnitOptions.find((o) => o.value === policy.executeSinceUnit) || undefined}
						            options={executeDelayUnitOptions}
						            onChange={(v) => policy.setExecuteSinceUnit(v?.value as SINCE_UNIT)}
						            validationGroup={validationGroup}
						            required
						            isSearchable={false}
					            />
				            </div>
			            </FormFieldLine>
		            )}
	            </>
            )}
        </>
    );
});

export default MarkAsDone;
